import { useState } from 'react';
import { testimonials } from '../../../mocks/testimonials';

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerView = 3;

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="testimonials" className="relative py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-green-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-green-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-green-400/10 rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16 relative">
          <div className="relative inline-block mb-8">
            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 relative z-10">
              What Our Customers Say
            </h2>
            <div className="absolute inset-0 bg-gradient-to-r from-green-600/10 via-green-500/10 to-green-600/10 blur-3xl transform scale-110 -z-10"></div>
          </div>

          <div className="relative mb-10">
            <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Don't just take our word for it - hear from our satisfied customers who trust us with their tree service needs
            </p>

            <div className="relative inline-block mt-8">
              <div className="bg-white/30 backdrop-blur-lg rounded-2xl p-6 shadow-2xl border border-white/20 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-green-600/5 via-green-500/5 to-green-400/5 animate-pulse"></div>
                <div className="relative z-10 flex items-center justify-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1 relative">
                    <i className="fas fa-star text-3xl text-yellow-400 transform hover:scale-110 transition-transform duration-300"></i>
                    <i className="fas fa-star text-3xl text-yellow-400 transform hover:scale-110 transition-transform duration-300"></i>
                    <i className="fas fa-star text-3xl text-yellow-400 transform hover:scale-110 transition-transform duration-300"></i>
                    <i className="fas fa-star text-3xl text-yellow-400 transform hover:scale-110 transition-transform duration-300"></i>
                    <i className="fas fa-star text-3xl text-yellow-400 transform hover:scale-110 transition-transform duration-300"></i>
                  </div>
                  <div className="flex flex-col items-center lg:items-start lg:ml-4">
                    <span className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-green-600 to-gray-900 bg-clip-text text-transparent">
                      5.0
                    </span>
                    <span className="text-lg font-semibold text-gray-900 opacity-80">from Happy Customers</span>
                  </div>
                </div>
                <div className="mt-4 flex justify-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2 bg-gradient-to-r from-green-500/10 to-green-600/10 backdrop-blur-sm rounded-full px-4 py-2 border border-green-300/30">
                    <i className="fas fa-shield-alt text-green-600 text-sm"></i>
                    <span className="text-sm font-medium text-green-700">Verified Reviews</span>
                  </div>
                  <div className="flex items-center gap-2 bg-gradient-to-r from-green-500/10 to-green-600/10 backdrop-blur-sm rounded-full px-4 py-2 border border-green-300/30">
                    <i className="fas fa-thumbs-up text-green-600 text-sm"></i>
                    <span className="text-sm font-medium text-green-700">Trusted Service</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <button
                  onClick={prevSlide}
                  className="group bg-white hover:bg-green-600 text-green-600 hover:text-white w-12 h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center border-2 border-green-600 cursor-pointer"
                >
                  <i className="fas fa-chevron-left text-lg group-hover:scale-110 transition-transform duration-300"></i>
                </button>
                <button
                  onClick={nextSlide}
                  className="group bg-white hover:bg-green-600 text-green-600 hover:text-white w-12 h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center border-2 border-green-600 cursor-pointer"
                >
                  <i className="fas fa-chevron-right text-lg group-hover:scale-110 transition-transform duration-300"></i>
                </button>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-gray-600 text-sm">
                  <span className="font-semibold text-green-600">{currentIndex + 1}</span> of{' '}
                  <span className="font-semibold text-green-600">{testimonials.length}</span> reviews
                </span>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full lg:w-1/3 md:w-1/2 flex-shrink-0 px-4">
                    <div className="group bg-gradient-to-br from-white to-gray-50/30 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-gray-200 hover:border-green-600/50 h-full relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-green-600/20 to-transparent rounded-bl-full"></div>
                      <div className="flex items-center mb-4">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                          style={{ background: testimonial.color }}
                        >
                          {testimonial.initial}
                        </div>
                        <div className="ml-3">
                          <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                          <div className="flex items-center gap-2">
                            <div className="flex text-yellow-400">
                              {[...Array(5)].map((_, i) => (
                                <i key={i} className="fas fa-star"></i>
                              ))}
                            </div>
                            <span className="text-gray-600 text-sm">{testimonial.meta}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-4">{testimonial.review}</p>
                      {testimonial.isNew && (
                        <div className="text-green-600 font-medium text-sm bg-gray-50 px-3 py-1 rounded-full inline-block shadow-sm">
                          ✨ New Review
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center mt-8">
              <ul className="flex items-center justify-center gap-3">
                {testimonials.map((_, index) => (
                  <li
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`h-5 w-5 rounded-full cursor-pointer transition-colors ${
                      index === currentIndex ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  ></li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-16">
            <div className="bg-white/95 backdrop-blur-lg rounded-3xl p-8 max-w-4xl mx-auto shadow-xl border border-gray-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/20 to-transparent rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-green-400/20 to-transparent rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                  Join Our Happy Customers Today
                </h3>
                <p className="text-gray-900 text-lg mb-6 opacity-80">
                  Experience the professional tree service that has earned us these outstanding reviews
                </p>
                <button
                  onClick={() => scrollToSection('quote')}
                  className="inline-flex items-center gap-3 bg-green-600 hover:bg-green-700 text-white px-10 py-5 rounded-xl text-xl font-bold transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 hover:scale-110 border-2 border-white/20 whitespace-nowrap cursor-pointer"
                >
                  <i className="fas fa-phone text-xl"></i>
                  Get Your Free Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
